import { useEffect, useState } from "react";
import ReactGridLayout, { Responsive, WidthProvider } from "react-grid-layout";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import useDeepCompareEffect from "use-deep-compare-effect";
import { useWindowSize } from "go-core/components/useWindowSize";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { DashboardEditActions } from "../../services/editMode/reducer";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import {
	DASHBOARD_EXTENDED_SM_LAYOUT,
	ORGANIZATION_DASHBOARD_GENERAL_SM_LAYOUT,
	USER_DASHBOARD_GENERAL_SM_LAYOUT,
	getDashboardSegmentNonMobileConfigForSegment,
} from "../../services/segments/constants";
import { DashboardSegmentActions } from "../../services/segments/reducer";
import { DashboardGridItem, DashboardType } from "../../services/types";
import { getOrderOfDashboardItems, shouldDisplayCallRecorderBox } from "../../utils/utils";
import DraggableDashboardAlerts from "../Alerts/DashboardAlerts";
import { DraggableDashboardCallRecorder } from "../CallRecorder/DashboardCallRecorder";
import DraggableDashboardClientsDetailsInformations from "../ClientsDetails/DashboardClientsDetails";
import DraggableDashboardLive from "../Live/DashboardLive";
import DraggableDashboardReportCategory from "../Reports/DashboardReportCategory";
import DraggableDashboardReportPayment from "../Reports/DashboardReportPayment";
import DraggableDashboardReportProduct from "../Reports/DashboardReportProduct";
import DraggableDashboardReportSales from "../Reports/DashboardReportSales";
import DraggableDashboardReportSalesAdvanced from "../Reports/DashbordReportSalesAdvanced";
import DashboardExtendedTab from "../Tabs/DashboardExtendedTab";
import DashboardGeneralTab from "../Tabs/DashboardGeneralTab";
import { tabType } from "../Tabs/DashboardTabs";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface Props {
	nextDataLoading: boolean;
	activeTab: tabType;
}

const getMobileLayout = (dashboardType: DashboardType, activeTab: tabType) => {
	if (activeTab === "EXTENDED") return DASHBOARD_EXTENDED_SM_LAYOUT;

	return dashboardType === DashboardType.GROUP_ORGANIZATIONS
		? USER_DASHBOARD_GENERAL_SM_LAYOUT
		: ORGANIZATION_DASHBOARD_GENERAL_SM_LAYOUT;
};

export const DraggableDashboardMainContent = ({ nextDataLoading, activeTab }: Props) => {
	const {
		state: {
			dashboardEdit: { nonMobileGridConfig },
			segmentsConfig: {
				selectedSegment,
				selectedSegment: {
					value: {
						dashboardEdit: { nonMobileGridConfig: selectedSegmentNonMobileConfig },
					},
				},
				allSegments,
			},
		},
		isEditMode,
		dispatch,
		dashboardType,
		isGridResettingMode,
		setIsGridResettingMode,
	} = useDashboardContext();
	const { isMobile } = useWindowSize();
	const [nonMobileLayout, setNonMobileLayout] = useState<ReactGridLayout.Layout[]>(nonMobileGridConfig);
	const [breakpoint, setBreakpoint] = useState<string>(isMobile ? "sm" : "lg");
	const layouts = {
		sm: getMobileLayout(dashboardType, activeTab),
		lg: nonMobileLayout,
	};
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const installedAppProviders = organization?.more?.installed_app_providers;
	const displayCallRecorderBox = shouldDisplayCallRecorderBox(dashboardType, installedAppProviders);

	const getSegmentFirstRowTheGreatestYCoordinate = () => {
		let theGreatestYCoordinate = 0;

		selectedSegmentNonMobileConfig.forEach((dahsboardGridItem) => {
			if (dahsboardGridItem.x === 0 && dahsboardGridItem.y > theGreatestYCoordinate) {
				theGreatestYCoordinate = dahsboardGridItem.y;
			}
		});

		return theGreatestYCoordinate + 1;
	};

	useEffect(() => {
		const dashboardItems = getOrderOfDashboardItems(dashboardType, installedAppProviders);
		const newNonMobileLayout = dashboardItems.map((dashboardItemName) => {
			const currentDashboardItemConfigFromSegment = selectedSegmentNonMobileConfig.find(
				(dashboardItemFromConfig) => dashboardItemFromConfig.i === dashboardItemName
			);
			const currentDashboardItemConfigFromCurrentLayout = nonMobileLayout.find(
				(dashboardItemFromLayout) => dashboardItemFromLayout.i === dashboardItemName
			);

			if (currentDashboardItemConfigFromSegment && currentDashboardItemConfigFromCurrentLayout) {
				const { w, h } = currentDashboardItemConfigFromCurrentLayout;
				const { i, x, y } = currentDashboardItemConfigFromSegment;

				return { i, x, y, w, h };
			} else if (currentDashboardItemConfigFromSegment && !currentDashboardItemConfigFromCurrentLayout) {
				const { i, x, y } = currentDashboardItemConfigFromSegment;

				return { i, x, y, w: 1, h: 1 };
			} else if (!currentDashboardItemConfigFromSegment && currentDashboardItemConfigFromCurrentLayout) {
				const { i, w, h } = currentDashboardItemConfigFromCurrentLayout;
				const y = getSegmentFirstRowTheGreatestYCoordinate();

				return { i, x: 0, y, w, h };
			}

			const y = getSegmentFirstRowTheGreatestYCoordinate();
			return { i: dashboardItemName, x: 0, y, w: 1, h: 1 };
		});

		setNonMobileLayout(newNonMobileLayout);
	}, [selectedSegment.name]);

	useDeepCompareEffect(() => {
		if (!isEditMode) {
			const newSegmentValue = JSON.parse(JSON.stringify(selectedSegment));

			const nonMobileGridConfigToBeComparedWithSegmentGridConfig = JSON.parse(
				JSON.stringify(getDashboardSegmentNonMobileConfigForSegment(nonMobileLayout))
			);

			const gridConfigsDiffer =
				JSON.stringify(nonMobileGridConfigToBeComparedWithSegmentGridConfig) !==
				JSON.stringify(selectedSegment.value.dashboardEdit.nonMobileGridConfig);

			if (gridConfigsDiffer) {
				newSegmentValue.value.dashboardEdit.nonMobileGridConfig =
					getDashboardSegmentNonMobileConfigForSegment(nonMobileLayout);

				dispatch({
					type: DashboardSegmentActions.CHANGE_SELECTED_SEGMENT,
					payload: newSegmentValue,
				});
				dispatch({
					type: DashboardEditActions.CHANGE_NON_MOBILE_GRID_CONFIG,
					payload: nonMobileLayout,
				});
			}
		}
	}, [dispatch, nonMobileLayout, isEditMode, location.search, allSegments, selectedSegment, nonMobileGridConfig]);

	useDeepCompareEffect(() => {
		if (isGridResettingMode) {
			const newNonMobileGridConfigBasedOnSegment = selectedSegment.value.dashboardEdit.nonMobileGridConfig.map(
				(dashboardGridItem, index) => {
					const { x, y, i } = dashboardGridItem;
					const { h, w } = nonMobileGridConfig[index];

					return { i, x, y, w, h };
				}
			);

			dispatch({
				type: DashboardEditActions.CHANGE_NON_MOBILE_GRID_CONFIG,
				payload: newNonMobileGridConfigBasedOnSegment,
			});
			setNonMobileLayout(newNonMobileGridConfigBasedOnSegment);
			setIsGridResettingMode(false);
		}
	}, [dispatch, isGridResettingMode, nonMobileGridConfig, selectedSegment.value.dashboardEdit.nonMobileGridConfig]);

	const layoutChangeHandler = (updatedLayout: ReactGridLayout.Layout[]) => {
		if (!isMobile) {
			const payload = updatedLayout.map((layoutObject) => {
				const { i, x, y, w, h } = layoutObject;

				return { i, x, y, w, h };
			});
			dispatch({ type: DashboardEditActions.CHANGE_NON_MOBILE_GRID_CONFIG, payload });
			setNonMobileLayout(updatedLayout);

			if (!isEditMode) {
				const segmentLayoutConfig = getDashboardSegmentNonMobileConfigForSegment(payload);

				dispatch({
					type: DashboardSegmentActions.CHANGE_SELECTED_SEGMENT_NON_MOBILE_GRID_CONFIG,
					payload: segmentLayoutConfig,
				});
			}
		}
	};
	const widthChangeHandler = (containerWidth: number) => {
		setBreakpoint(containerWidth < 709 ? "sm" : "lg");
	};

	if (isMobile || breakpoint === "sm") {
		if (activeTab === "GENERAL") {
			return (
				<>
					<ResponsiveGridLayout onWidthChange={widthChangeHandler} />
					<DashboardGeneralTab loading={nextDataLoading} />
				</>
			);
		}

		return <DashboardExtendedTab />;
	}

	return (
		<ResponsiveGridLayout
			layouts={layouts}
			cols={{ lg: 3, sm: 1 }}
			// TODO: change to {lg: 709(element px size on 812px window size), sm: 0} on draggable mobile layout
			breakpoints={{ lg: 50, sm: 0 }}
			rowHeight={10}
			margin={[16, 16]}
			containerPadding={[0, 0]}
			isDraggable={isEditMode}
			onLayoutChange={layoutChangeHandler}
			onWidthChange={widthChangeHandler}
			draggableCancel="a"
			isBounded
		>
			{dashboardType === DashboardType.ORGANIZATION && (
				<DraggableDashboardAlerts key={DashboardGridItem.ALERTS} setNonMobileLayout={setNonMobileLayout} />
			)}
			<DraggableDashboardClientsDetailsInformations
				key={DashboardGridItem.CLIENT_DETAILS}
				setNonMobileLayout={setNonMobileLayout}
			/>
			{displayCallRecorderBox && (
				<DraggableDashboardCallRecorder
					key={DashboardGridItem.CALL_RECORDER}
					setNonMobileLayout={setNonMobileLayout}
				/>
			)}
			{dashboardType === DashboardType.ORGANIZATION && (
				<DraggableDashboardLive
					loading={nextDataLoading}
					key={DashboardGridItem.LIVE}
					setNonMobileLayout={setNonMobileLayout}
				/>
			)}
			<DraggableDashboardReportSalesAdvanced
				key={DashboardGridItem.SALES_ADVANCED}
				setNonMobileLayout={setNonMobileLayout}
			/>
			<DraggableDashboardReportPayment key={DashboardGridItem.PAYMENT} setNonMobileLayout={setNonMobileLayout} />
			<DraggableDashboardReportProduct key={DashboardGridItem.PRODUCT} setNonMobileLayout={setNonMobileLayout} />
			<DraggableDashboardReportCategory
				key={DashboardGridItem.CATEGORY}
				setNonMobileLayout={setNonMobileLayout}
			/>
			<DraggableDashboardReportSales key={DashboardGridItem.SALES} setNonMobileLayout={setNonMobileLayout} />
		</ResponsiveGridLayout>
	);
};
